import { InformationCircleIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import DisclaimerText from 'components/DisclaimerText';

import industryToReadable from 'utils/enums/industry-to-readable';
import { formatCurrencyToShort, formatNumber } from 'utils/format-helper';

import { useSyndicateAnalyticsData } from '.';

const SECTOR_CHART_COLORS = [
  '#4f46e5',
  '#7c3aed',
  '#0f766e',
  '#ea580c',
  '#db2777',
  '#0284c7',
  '#65a30d',
  '#dc2626',
];

function formatMetricValue(
  type: 'currency' | 'multiple' | 'percentage' | 'number',
  value?: number | null
) {
  if (value === undefined || value === null) return '-';

  if (type === 'currency') return formatCurrencyToShort(value);
  if (type === 'multiple') return `${value.toFixed(2)}x`;
  if (type === 'percentage') return `${value.toFixed(2)}%`;

  return formatNumber(value);
}

function MetricCard({
  label,
  value,
  helperText,
  note,
  onClick,
  tone = 'slate',
}: {
  label: string;
  value: string;
  helperText?: string;
  note?: ReactNode;
  onClick?: () => void;
  tone?: 'slate' | 'indigo' | 'violet' | 'emerald' | 'amber';
}) {
  const toneStyles = {
    slate: {
      container: 'border-slate-200 bg-slate-50/70',
      label: 'text-slate-500',
      value: 'text-slate-900',
      helper: 'text-slate-500',
      action: 'text-slate-500',
    },
    indigo: {
      container: 'border-indigo-100 bg-indigo-50/60',
      label: 'text-indigo-600',
      value: 'text-slate-900',
      helper: 'text-slate-500',
      action: 'text-indigo-600',
    },
    violet: {
      container: 'border-violet-100 bg-violet-50/60',
      label: 'text-violet-600',
      value: 'text-slate-900',
      helper: 'text-slate-500',
      action: 'text-violet-600',
    },
    emerald: {
      container: 'border-emerald-100 bg-emerald-50/60',
      label: 'text-emerald-600',
      value: 'text-slate-900',
      helper: 'text-slate-500',
      action: 'text-emerald-600',
    },
    amber: {
      container: 'border-amber-100 bg-amber-50/60',
      label: 'text-amber-600',
      value: 'text-slate-900',
      helper: 'text-slate-500',
      action: 'text-amber-600',
    },
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        'h-full rounded-3xl border p-6 text-left shadow-sm transition duration-200',
        toneStyles.container,
        onClick
          ? 'hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-200'
          : 'cursor-default'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <p
            className={classNames(
              'text-xs font-semibold uppercase tracking-[0.2em]',
              toneStyles.label
            )}
          >
            {label}
          </p>
          {note && (
            <div className="group relative inline-block">
              <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              <div
                className="invisible group-hover:visible absolute z-50 w-64 bg-indigo-600 text-white text-xs text-left rounded p-2.5 -left-2 top-6 normal-case whitespace-pre-line"
                style={{ transform: 'translateZ(0)' }}
              >
                {note}
                <div className="absolute -top-1 left-3 w-2 h-2 bg-indigo-600 rotate-45" />
              </div>
            </div>
          )}
        </div>
        {onClick && (
          <span className={classNames('text-xs font-medium', toneStyles.action)}>Details</span>
        )}
      </div>
      <p className={classNames('mt-4 text-3xl font-semibold tracking-tight', toneStyles.value)}>
        {value}
      </p>
      {helperText && (
        <p className={classNames('mt-2 text-sm leading-6', toneStyles.helper)}>{helperText}</p>
      )}
    </button>
  );
}

function SectorTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;

  const sector = payload[0].payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
      <p className="text-sm font-semibold text-slate-900">{sector.name}</p>
      <p className="mt-1 text-sm text-slate-600">{formatCurrencyToShort(sector.value)} invested</p>
      <p className="text-sm text-slate-500">{sector.share.toFixed(2)}% of total invested capital</p>
    </div>
  );
}

const Overview: FC = () => {
  const navigate = useNavigate();
  const { analytics, syndicateId } = useSyndicateAnalyticsData();

  const sectorAllocation = useMemo(() => {
    const totals = new Map<string, number>();

    analytics.yearWisePerformance.forEach(year => {
      year.sectorBreakdown.forEach(item => {
        const allocatedCapital = (year.investedCapital * item.percentage) / 100;

        if (!allocatedCapital) return;

        const industry = industryToReadable(item.industry as any);

        totals.set(industry.label, (totals.get(industry.label) || 0) + allocatedCapital);
      });
    });

    const total = Array.from(totals.values()).reduce((sum, value) => sum + value, 0);

    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], index) => ({
        name,
        value,
        share: total > 0 ? (value / total) * 100 : 0,
        color: SECTOR_CHART_COLORS[index % SECTOR_CHART_COLORS.length],
      }));
  }, [analytics.yearWisePerformance]);

  const topSector = sectorAllocation[0];
  const additionalSectorCount = Math.max(sectorAllocation.length - 6, 0);

  return (
    <div className="mb-12">
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 my-6">
        <MetricCard
          label="DPI"
          value={formatMetricValue('multiple', analytics.overview.dpi)}
          note={
            <>
              DPI = Total realized value / Total invested amount.
              <br />
              <br />
              Exits in progress may not be considered
            </>
          }
          onClick={() => navigate(`/syndicate-dashboard/${syndicateId}/analytics/dpi-breakdown`)}
          tone="indigo"
        />
        <MetricCard
          label="XIRR"
          value={formatMetricValue('percentage', analytics.overview.xirr)}
          note={
            <>
              XIRR is the annualized return computed from dated cash flows, including investments
              and realized/unrealized outcomes.
              <br />
              <br />
              Sensitive to cash flow timing - large early exits or young vintages can produce
              unusually high or low values; best read alongside DPI and TVPI.
            </>
          }
          tone="indigo"
        />
        <MetricCard
          label="TVPI"
          value={formatMetricValue('multiple', analytics.overview.tvpi)}
          note="TVPI = (Total realized value + Total unrealized value) / Total invested amount."
          tone="violet"
        />
        <MetricCard
          label="RVPI"
          value={formatMetricValue('multiple', analytics.overview.rvpi)}
          note="RVPI = Total unrealized value / Total invested amount."
          tone="emerald"
        />
        <MetricCard
          label="Markup Rate"
          value={formatMetricValue('percentage', analytics.overview.markupRate)}
          note="Markup Rate = (Number of deals where total value exceeds invested capital / Total number of deals) * 100."
          tone="amber"
        />
        <MetricCard
          label="Total Invested"
          value={formatMetricValue('currency', analytics.overview.totalInvestedAmount)}
          note="Sum of invested capital across all underlying deals in the syndicate."
          tone="slate"
        />
        <MetricCard
          label="Total Realized"
          value={formatMetricValue('currency', analytics.overview.totalRealizedValue)}
          note={
            <>
              Cumulative distributions and exits received from portfolio investments.
              <br />
              <br />
              Exits in progress may not be considered
            </>
          }
          tone="emerald"
        />
        <MetricCard
          label="Total Unrealized"
          value={formatMetricValue('currency', analytics.overview.totalUnrealizedValue)}
          note="Current marked value of active, not-yet-realized portfolio positions."
          tone="violet"
        />
        <MetricCard
          label="Portfolio Coverage"
          value={`${formatMetricValue(
            'number',
            analytics.overview.numberOfDeals
          )} / ${formatMetricValue('number', analytics.overview.numberOfCompanies)}`}
          note="Displayed as Number of deals / Number of unique portfolio companies."
          helperText="Deals / Companies"
          tone="slate"
        />
      </section>

      <DisclaimerText
        message={
          <>
            <p>Markups are based on share price changes linked to corporate actions.</p>
            <p>
              Active markups are updated monthly (by the 15th of the following month, reflecting
              reported corporate actions and price movements through the end of the prior month);
              exited investments are finalised on distribution (if any) and after distribution
              statements are issued.
            </p>
            <p>All figures are computed in real time and shown gross of carry.</p>
          </>
        }
      />

      <section className="rounded-3xl border border-slate-200 p-6 shadow-sm my-20 bg-white">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            Sector-wise investment allocation
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Aggregated using the sector mix from every investment year, rolled up into a single
            all-years allocation view.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:items-center">
          {sectorAllocation.length ? (
            <>
              <div className="relative isolate mx-auto h-80 w-full max-w-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sectorAllocation}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={90}
                      outerRadius={116}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {sectorAllocation.map(item => (
                        <Cell key={item.name} fill={item.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      wrapperStyle={{ zIndex: 30 }}
                      content={({ active, payload }) => (
                        <SectorTooltip active={active} payload={payload} />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                    All years
                  </p>
                  <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                    {formatMetricValue('currency', analytics.overview.totalInvestedAmount)}
                  </p>
                  <p className="mt-2 max-w-[120px] text-xs text-slate-500">
                    deployed capital represented in the sector mix
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-sm font-medium text-slate-500">Largest allocation</p>
                  <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
                    <p className="text-2xl font-semibold tracking-tight text-slate-900">
                      {topSector?.name || '-'}
                    </p>
                    <p className="text-sm font-medium text-slate-500">
                      {topSector ? `${topSector.share.toFixed(2)}% of invested capital` : '-'}
                    </p>
                  </div>
                </div>

                {sectorAllocation.slice(0, 6).map(sector => (
                  <div key={sector.name} className="rounded-2xl border border-slate-100 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: sector.color }}
                        />
                        <p className="text-sm font-medium text-slate-900">{sector.name}</p>
                      </div>
                      <p className="text-sm font-medium text-slate-500">
                        {sector.share.toFixed(2)}%
                      </p>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(sector.share, 100)}%`,
                          backgroundColor: sector.color,
                        }}
                      />
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      {formatCurrencyToShort(sector.value)} invested
                    </p>
                  </div>
                ))}

                {additionalSectorCount > 0 && (
                  <p className="text-sm text-slate-500">
                    + {additionalSectorCount} more sector
                    {additionalSectorCount > 1 ? 's' : ''} in the overall allocation.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500 lg:col-span-2">
              Sector allocation will appear here once investments with sector mapping are available.
            </div>
          )}
        </div>

        <div className="mt-8 mb-2">
          <DisclaimerText
            message={
              <>
                <p>
                  Sector classifications are sourced from the AngelList India database, populated by
                  the syndicate lead via the Information tab on each deal
                </p>
              </>
            }
          />
        </div>
      </section>
    </div>
  );
};

export default Overview;
